import React, {useEffect, useState} from 'react'
import { useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import { coApplicantDeclineInvitation, getCoApplicantInviteStatus, verifyCoApplicantInvite } from '../../api'
import '../../style/application.scss'
import {MobileNumberInput} from '../../../common/component/MobileNumberInput'
import {OTPInput} from '../../../common/component/OTPInput'
import { IApplicationFormData, normalizeApplicationFormData } from '../../models/application-form'
import { dateFormat } from '../../../../shared/service/utils'
import { buildCoApplicantInviteInitialData, getCoApplicantInvite, getFirstName, getPrimaryApplicantName } from './coApplicantInviteContext'
import DooraHelperModal from '../landing/DooraHelperModal'
import ThisIsntMeModal from './ThisIsntMeModal'

const AU_MOBILE_REGEX = /^04\d{8}$/
const DEFAULT_NEXT_STEP_PATH = 'profile'
const VERIFIED_REDIRECT_SECONDS = 5
const START_PATH = '/application-wizard/start'
const APPLICATION_LANDING_PATH = '/application-landing'

const normalizeMobile = (value: string): string => value.replace(/\D/g, '')

type CoApplicantInviteVerifyForm = {
  mobileNumber: string
}

export default function CoApplicantInviteVerifyPage() {
  const history = useHistory()
  const {inviteId} = useParams<{inviteId: string}>()
  const {
    control,
    handleSubmit,
    formState: {errors},
  } = useForm<CoApplicantInviteVerifyForm>({
    defaultValues: {
      mobileNumber: '',
    },
  })
  const [otpCode, setOtpCode] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [retryAfterSeconds, setRetryAfterSeconds] = useState(0)
  const [isCheckingInviteStatus, setIsCheckingInviteStatus] = useState(true)
  const [isAlreadyVerified, setIsAlreadyVerified] = useState(false)
  const [redirectSeconds, setRedirectSeconds] = useState(VERIFIED_REDIRECT_SECONDS)
  const [isExpired, setIsExpired] = useState(false);
  const [isDeclined, setIsDeclined] = useState(false);
  const [isInvalidInvite, setIsInvalidInvite] = useState(false);
  const [view, setView] = useState("splashScreen");
  const [primaryApplicant, setPrimaryApplicant] = useState<Partial<IApplicationFormData> | null>(null)
  const [isHelperOpen, setIsHelperOpen] = useState(false)
  const [isNotMeOpen, setIsNotMeOpen] = useState(false)
  const [isDeclining, setIsDeclining] = useState(false)

  const primaryApplicantName = getPrimaryApplicantName(primaryApplicant);
  const primaryApplicantFirstName = getFirstName(primaryApplicantName)|| 'Someone';
  const currentCoApplicant = getCoApplicantInvite(primaryApplicant, inviteId);
  const isUnavailableInvite = isAlreadyVerified || isDeclined || isExpired || isInvalidInvite;
  const unavailableInvite = isAlreadyVerified ? 'Already verified' : isDeclined ? 'Invitation declined' : isExpired ? 'Invitation expired' : 'Invitation unavailable';

  useEffect(() => {
    if (retryAfterSeconds <= 0) {
      return
    }

    const timer = window.setInterval(() => {
      setRetryAfterSeconds((seconds) => Math.max(0, seconds - 1))
    }, 1000)

    return () => window.clearInterval(timer)
  }, [retryAfterSeconds])

   useEffect(() => {
        let isActive = true
        const normalizedInviteId = inviteId?.trim() ?? ''

        const checkInviteStatus = async () => {
            if (!normalizedInviteId) {
                if (isActive) {
                    setIsInvalidInvite(true);
                    setIsAlreadyVerified(false);
                    setIsExpired(false);
                    setIsDeclined(false);
                    setPrimaryApplicant(null);
                    setMessage('Invite link is invalid.');
                    setRedirectSeconds(VERIFIED_REDIRECT_SECONDS)
                    setIsCheckingInviteStatus(false)
                }
                return
            }

            try {
                const result = await getCoApplicantInviteStatus(normalizedInviteId)

                if (!isActive) {
                    return
                }

                if (!result.found) {
                    setIsInvalidInvite(true);
                    setIsAlreadyVerified(false);
                    setIsExpired(false);
                    setIsDeclined(false);
                    setPrimaryApplicant(null);
                    setMessage(
                        "We couldn't find this invitation. It may have expired or been replaced by a newer invitation."
                    );
                    setRedirectSeconds(VERIFIED_REDIRECT_SECONDS);
                }
                else if (result.found && result.isVerified) {
                    setIsAlreadyVerified(true);
                    setIsExpired(false);
                    setIsDeclined(false);
                    setIsInvalidInvite(false);
                    setPrimaryApplicant(null);
                    setMessage('This invite has already been verified.');
                    setRedirectSeconds(VERIFIED_REDIRECT_SECONDS);
                }
                else if (result.found && result.isDeclined) {
                    setIsDeclined(true);
                    setIsAlreadyVerified(false);
                    setIsExpired(false);
                    setIsInvalidInvite(false);
                    setPrimaryApplicant(null);
                    setMessage('This invite has already been declined.');
                    setRedirectSeconds(VERIFIED_REDIRECT_SECONDS);
                }
                else if (result.found && result.isExpired) {
                    setIsExpired(true);
                    setIsAlreadyVerified(false);
                    setIsDeclined(false);
                    setIsInvalidInvite(false);
                    setPrimaryApplicant(null);
                    setMessage('Your verification link has expired. Please ask the primary applicant to resend the invitation.');
                    setRedirectSeconds(VERIFIED_REDIRECT_SECONDS)
                }
                else {
                    setIsAlreadyVerified(false);
                    setIsDeclined(false);
                    setIsExpired(false);
                    setIsInvalidInvite(false);
                    setMessage(null);
                    setPrimaryApplicant(normalizeApplicationFormData(result.primaryApplicant))
                }
            } catch {
                //Allow manual verification even when status pre-check fails.
            } finally {
                if (isActive) {
                    setIsCheckingInviteStatus(false)
                }
            }
        }

        checkInviteStatus()
        return () => {
          isActive = false
      }
  }, [inviteId])

  useEffect(() => {
    if(!isAlreadyVerified && !isDeclined && !isExpired && !isInvalidInvite) {
        return
    }

    if (redirectSeconds <= 0) {
      history.replace(isAlreadyVerified ? START_PATH : APPLICATION_LANDING_PATH)
      return
    }

    const timer = window.setTimeout(() => {
        setRedirectSeconds((seconds) => Math.max(0, seconds - 1))
    }, 1000)

    return () => window.clearTimeout(timer)
  }, [history, isAlreadyVerified, redirectSeconds, isDeclined, isExpired, isInvalidInvite])

  const handleVerify = async ({mobileNumber}: CoApplicantInviteVerifyForm) => {
    if (isCheckingInviteStatus || isUnavailableInvite || isSubmitting || retryAfterSeconds > 0) {
      return
    }

    setMessage(null)

    const normalizedMobile = normalizeMobile(mobileNumber)
    if (!AU_MOBILE_REGEX.test(normalizedMobile)) {
      setMessage('Enter a valid Australian mobile number.')
      return
    }

    const normalizedCode = otpCode.trim()
    if (normalizedCode.length > 0 && normalizedCode.length !== 6) {
      setMessage('Enter the 6-digit verification code.')
      return
    }

    if (!inviteId?.trim()) {
      setMessage('Invite link is invalid.')
      return
    }

    setIsSubmitting(true)
    try {
        const result = await verifyCoApplicantInvite({ inviteId, mobileNumber: normalizedMobile, otpCode: normalizedCode })

      if (!result.verified || !result.sessionId || (!result.applicationId && result.nextStepPath !== 'household-warning')) {
        setMessage(result.failureCode ?? 'Unable to verify invite right now.')
        if (result.retryAfterSeconds && result.retryAfterSeconds > 0) {
          setRetryAfterSeconds(result.retryAfterSeconds)
        }
        return
      }

      const nextStep = result.nextStepPath === 'household-warning'
        ? DEFAULT_NEXT_STEP_PATH
        : result.nextStepPath?.trim() || DEFAULT_NEXT_STEP_PATH
      history.replace(
        `/application-wizard/${nextStep}?sessionId=${encodeURIComponent(result.sessionId)}`
      )
    } catch {
      setMessage('Unable to verify invite right now.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInvalidSubmit = () => {
    if (isCheckingInviteStatus || isUnavailableInvite) {
      return
    }

    setMessage('Enter a valid Australian mobile number.')
  }

  const acceptInvitation = () => {
      const normalizedInviteId = inviteId?.trim();

      if (!normalizedInviteId || !primaryApplicant || !currentCoApplicant) {
          setMessage('Invite details could not be loaded. Refresh and try again.')
          return;
      }

      const initialAppliationData = buildCoApplicantInviteInitialData(primaryApplicant, normalizedInviteId)

      history.push({
          pathname: '/application-wizard/create-account',
          search: `?inviteId=${encodeURIComponent(normalizedInviteId)}`,
          state: {
              initialAppliationData
          }
      })
  }

  const declineInvitation = async () => {
      const normalizedInviteId = inviteId?.trim();
      if (!normalizedInviteId) {
          setMessage('Invite details could not be loaded. Refresh and try again.');
          return;
      }

      setIsDeclining(true);

      try {
          const response = await coApplicantDeclineInvitation({ inviteId: normalizedInviteId });
          if (!response.success) {
              setMessage(response.message ?? 'Unable to decline invite right now.')
              return
          }

          history.push('/application-landing');
      }
      catch {
          setMessage('Unable to decline invite right now.');
      }
      finally {
          setIsDeclining(false);
      }
  }

    return (
        <>
            {view === 'verify' ? (
                <main className='wizard-page px-3 px-md-6 py-6 py-md-10'>
                    <div className='mx-auto w-100' style={{ maxWidth: 560 }}>
                        <form
                            onSubmit={handleSubmit(handleVerify, handleInvalidSubmit)}
                            className='wizard-card bg-white rounded-4 shadow-sm'
                        >
                            <div className='p-4 p-md-6'>
                                <div className='text-center mb-8'>
                                    <h2 className='fw-semibold fs-5 mb-2'>Verify Co-Applicant Invite</h2>
                                    {isUnavailableInvite ? (
                                        <p className='text-muted mb-0'>
                                            {unavailableInvite}. Redirecting in {redirectSeconds}s...
                                        </p>
                                    ) : (
                                        <p className='text-muted mb-0'>Enter your mobile number and verification code.</p>
                                    )}
                                </div>

                                {!isCheckingInviteStatus && isUnavailableInvite && (
                                    <>
                                        <MobileNumberInput<CoApplicantInviteVerifyForm>
                                            name='mobileNumber'
                                            label='Mobile Number'
                                            placeholder='0412 345 678'
                                            control={control}
                                            errors={errors}
                                            required
                                            rules={{
                                                required: 'Enter a valid Australian mobile number.',
                                                validate: (value) =>
                                                    AU_MOBILE_REGEX.test(normalizeMobile(String(value))) ||
                                                    'Enter a valid Australian mobile number.',
                                            }}
                                        />

                                        <div className='mb-2'>
                                            <label className='form-label fw-semibold'>Verification Code</label>
                                            <OTPInput
                                                name='coApplicantOtpCode'
                                                length={6}
                                                numericOnly={true}
                                                disabled={isSubmitting || retryAfterSeconds > 0}
                                                required='OTP is required'
                                                onChange={(value) => setOtpCode(value)}
                                                className='my-2 px-20'
                                            />
                                        </div>
                                    </>
                                )}

                                {isCheckingInviteStatus && (
                                    <div className='small text-muted mt-2'>Checking invite status...</div>
                                )}

                                {!isUnavailableInvite && retryAfterSeconds > 0 && (
                                    <div className='small text-muted mt-2'>
                                        Please wait {retryAfterSeconds}s before trying again.
                                    </div>
                                )}

                                {message && (
                                    <div className={`mt-3 ${isAlreadyVerified ? 'text-success' : 'text-danger'}`}>
                                        {message}
                                    </div>
                                )}
                            </div>

                            {!isCheckingInviteStatus && !isUnavailableInvite && (
                                <div className='wizard-footer border-top p-4 p-md-5'>
                                    <button
                                        type='submit'
                                        className='btn btn-doora wizard-primary-action w-100'
                                        disabled={isSubmitting || retryAfterSeconds > 0}
                                    >
                                        {isSubmitting ? 'Verifying...' : 'Verify & Continue'}
                                    </button>
                                </div>
                            )}
                        </form>
                    </div>
                </main>
            ) : (
                <div className='splash-screen-page'>
                    <div className='splash-screen-bg' aria-hidden='true'>
                        <span className='application-landing-orb application-landing-orb-left' />
                        <span className='application-landing-orb application-landing-orb-center' />
                        <span className='application-landing-orb application-landing-orb-right' />
                    </div>

                    <main className='splash-screen-content'>
                        <div>
                            <img src='/media/logos/doora-logo.png' alt='Doora' className='application-landing-logo' />
                        </div>
                        {!isCheckingInviteStatus && !isUnavailableInvite ? (
                            <>
                                  <>
                                    <div className="splash-screen-pill">
                                      <span aria-hidden='true'></span>
                                          You've been invited
                                    </div>
                                    <h1 className='splash-screen-title'><span>{primaryApplicantFirstName}</span> wants you on the lease</h1>
                                    <section className="splash-screen-card">
                                      <h2>What you're joining</h2>
                                      <div className="splash-screen-detail">
                                        <i className="bi bi-people" aria-hidden='true'></i>
                                        <div>
                                          <span>Application</span>
                                          <strong>{primaryApplicant?.applicationType}</strong>
                                        </div>
                                      </div>
                                      <div className="splash-screen-detail">
                                        <i className="bi bi-calendar" aria-hidden='true'></i>
                                        <div>
                                          <span>Move-in</span>
                                          <div className="splash-screen-movein">
                                            <strong>{primaryApplicant?.moveInAsap ? "ASAP" : dateFormat(primaryApplicant?.moveInPreferredDate)}</strong>
                                            <span aria-hidden="true"></span>
                                            <strong>{primaryApplicant?.preferredLeaseTerm}</strong>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="splash-screen-detail">
                                        <i className="bi bi-currency-dollar" aria-hidden='true'></i>
                                        <div>
                                          <span>Budget</span>
                                          <strong>${primaryApplicant?.rentWeeklyBudgetMin} - ${primaryApplicant?.rentWeeklyBudgetMax} / week</strong>
                                        </div>
                                      </div>
                                      <div className="splash-screen-detail">
                                        <i className="bi bi-person-check" aria-hidden='true'></i>
                                        <div>
                                          <span>On the lease</span>
                                          <div className="splash-screen-lease">
                                            <strong>{primaryApplicant?.fullName}</strong>
                                            {primaryApplicant?.coApplicants?.map(coApplicant => (
                                              <React.Fragment key={coApplicant.inviteId}>
                                                <span aria-hidden="true"></span>
                                                {coApplicant.inviteId === inviteId ? (
                                                   <strong>you ({coApplicant?.fullName})</strong>
                                                     ) : (<strong>{coApplicant?.fullName}</strong>)}
                                                         </React.Fragment>
                                                     ))}
                                                 </div>
                                            </div>
                                        </div>
                                      <div className="splash-screen-detail">
                                        <i className="bi bi-lock" aria-hidden='true'></i>
                                        <div>
                                          <span>Privacy</span>
                                          <strong>{mapRetentionText(primaryApplicant?.retentionPreference)}</strong>
                                        </div>
                                      </div>
                                      <div className="splash-screen-invited">
                                        Invited by {primaryApplicantName} {formatInvitedAgo(primaryApplicant?.coApplicants?.find(x => x.inviteId === inviteId)?.invitedAtUtc)}
                                      </div>
                                    </section>
                                    <ul className="splash-screen-trust-content">
                                      {trustItems.map((item) => (
                                        <li key={item.label} className="splash-screen-trust-item">
                                          <span className="splash-screen-trust-icon" style={{ color: item.color }} aria-hidden='true'>
                                            {item.icon}
                                          </span>
                                          <span className="splash-screen-trust-label">{item.label}</span>
                                            </li>
                                      ))}
                                    </ul>

                                    <button className='btn splash-screen-cta' onClick={acceptInvitation}>
                                      Accept Invitation
                                    </button>

                                    <button type='button' className='btn btn-link application-landing-help-link mt-0' onClick={() => setIsNotMeOpen(true)}>
                                      This isn't me <span aria-hidden='true'>&rarr;</span>
                                    </button>
                                    <br />
                                    <button type='button' className='btn btn-link application-landing-help-link mt-0' onClick={() => setIsHelperOpen(true)}>
                                      Questions? Talk to a real person <span aria-hidden='true'>&rarr;</span>
                                    </button>
                                    {isHelperOpen && <DooraHelperModal onClose={() => setIsHelperOpen(false)} />}
                                    {isNotMeOpen && <ThisIsntMeModal onClick={declineInvitation} onClose={() => setIsNotMeOpen(false)} message={message} isDeclining={isDeclining} />}
                                  </>
                            </>
                            ) : (
                                <>
                                    <h1 className='splash-screen-title'>
                                        {isCheckingInviteStatus ? 'Checking invite status...' : `${unavailableInvite}. Redirecting in ${redirectSeconds}s... `}
                                        </h1>
                                    {!isCheckingInviteStatus && message && (
                                        <p className='splash-screen-subtitle'>{message}</p>
                                    )}
                                </>
                            )}
                    </main>
                </div>
            )}
        </>
    )
}

const trustItems = [
    {
        label: 'Encrypted',
        color: '#F0A9D8',
        icon: (
            <svg width='20' height='20' viewBox='0 0 20 20' fill='none' aria-hidden='true'>
                <path
                    d='M10 2.5l6 2v5c0 3.8-2.6 7.2-6 8.5-3.4-1.3-6-4.7-6-8.5v-5l6-2z'
                    stroke='currentColor'
                    strokeWidth='1.6'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                />
            </svg>
        ),
    },
    {
        label: 'Never sold',
        color: '#53BCFF',
        icon: (
            <svg width='20' height='20' viewBox='0 0 20 20' fill='none' aria-hidden='true'>
                <circle
                    cx='10'
                    cy='10'
                    r='7'
                    stroke='currentColor'
                    strokeWidth='1.6'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                />
                <path
                    d='M12 7.3c-.5-.8-1.3-1.3-2.3-1.3-1.3 0-2.2.8-2.2 1.8 0 1 .9 1.5 2.2 1.9 1.5.4 2.6.9 2.6 2.1 0 1.1-1 2-2.5 2-1 0-1.9-.4-2.4-1.2M10 4.8v1.4M10 13.8v1.4'
                    stroke='currentColor'
                    strokeWidth='1.6'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                />
                <path
                    d='M5 15L15 5'
                    stroke='currentColor'
                    strokeWidth='1.9'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                />
            </svg>
        ),
    },
    {
        label: 'You delete it',
        color: '#BB9AE4',
        icon: (
            <svg width='20' height='20' viewBox='0 0 20 20' fill='none' aria-hidden='true'>
                <path
                    d='M4 5.5h12M8 5.5V4a1.5 1.5 0 011.5-1.5h1A1.5 1.5 0 0112 4v1.5M5.5 5.5l.8 10A1.5 1.5 0 007.8 17h4.4a1.5 1.5 0 001.5-1.5l.8-10'
                    stroke='currentColor'
                    strokeWidth='1.6'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                />
            </svg>
        ),
    },
]

const mapRetentionText = (value: number | null | undefined) => {
    switch (value) {
        case 1:
            return "Keep it active";
        case 2:
            return "Auto-delete after 90 days";
        case 3:
            return "Auto-delete after 60 days";
        default:
            return "Keep it active";
    }
}

const formatInvitedAgo = (value: string | undefined) => {
    if (!value) return ''

    const parsed = new Date(value + "Z")
    if (Number.isNaN(parsed.getTime())) {
        return ''
    }

    const diffMs = Date.now() - parsed.getTime()

    if (diffMs <= 0) {
        return 'Just now'
    }

    const totalMinutes = Math.floor(diffMs / 60_000)
    const days = Math.floor(totalMinutes / (60 * 24))
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60)
    const minutes = totalMinutes % 60

    if (days > 0) {
        return `${days} ${days === 1 ? 'day' : 'days'}${hours > 0 ? ` ${hours} ${hours === 1 ? 'hour' : 'hours'}` : ''} ago`
    }

    if (hours > 0) {
        return `${hours} ${hours === 1 ? 'hour' : 'hours'}${minutes > 0 ? ` ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}` : ''} ago`
    }

    return `${minutes > 0 ? ` ${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago` : 'just now'}`;
}
